import styled, { css, DefaultTheme, keyframes } from "styled-components";
import { theme } from "../../../constants/StyledComponents";
import { InputSizes } from "../Input/Input";
import { SelectVariantEnum } from "./Select";

const fadeInSlideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInSlideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const sizeStyles = (theme: DefaultTheme, size = InputSizes.REGULAR) =>
  ({
    small: css`
      height: ${theme.variables.height_1};
    `,
    medium: css`
      height: ${theme.variables.height_2};
    `,
    regular: css`
      height: ${theme.variables.height_3};
    `,
  })[size];

const SelectContainer = styled.div<{ $size?: InputSizes }>`
  ${({ theme, $size: size }) => sizeStyles(theme, size)}
  position: relative;
  cursor: pointer;
`;

const StyledInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  cursor: pointer;
`;

const StyledOptionsList = styled.ul<{ $flipUp?: boolean; $fixed?: boolean }>`
  position: absolute;
  width: 100%;
  max-width: ${({ $fixed }) => ($fixed ? "calc(100vw - 16px)" : "none")};
  border: 1px solid ${theme.colors.black};
  border-radius: ${theme.variables.border_radius_1};
  background-color: ${theme.colors.white};
  z-index: 9990;
  overflow: hidden;
  padding: 0;
  list-style: none;
  animation: ${({ $flipUp }) => ($flipUp ? fadeInSlideUp : fadeInSlideDown)}
    0.1s;
  margin-top: ${({ $fixed }) => ($fixed ? "0" : "2px")};
  margin: ${({ $fixed }) => $fixed === true && "0"};
`;

const StyledOptionsScrollArea = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 8px 0;
`;

const StyledOptionItemContainer = styled.li`
  padding: 0 8px;
`;

/* variants */
const variantStyles = (
  theme: DefaultTheme,
  variant = SelectVariantEnum.DEFAULT,
  disabled?: boolean,
) =>
  ({
    default: css`
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      background-color: transparent;
      &:hover {
        background-color: ${disabled ? "transparent" : theme.colors.purple_2};
      }
    `,
    single_select: css`
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;

      &:hover {
        background-color: ${disabled ? "transparent" : theme.colors.purple_2};
      }
    `,
    multi_select: css`
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;

      &:hover {
        background-color: ${disabled ? "transparent" : theme.colors.purple_2};
      }
    `,
  })[variant];

const StyledOptionItem = styled.div<{
  $variant: SelectVariantEnum;
  $disabled?: boolean;
}>`
  ${({ theme, $variant: variant, $disabled }) =>
    variantStyles(theme, variant, $disabled)}
  padding: 8px;
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  border-radius: ${theme.variables.border_radius_1};
`;

const StyledCloseContainer = styled.div<{ $visible: boolean }>`
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
`;

const StyledSearchInputContainer = styled.div`
  padding: 0 8px 8px 8px;
`;

const StyledLoaderEmptyContent = styled.div`
  height: 50px;
`;

const StyledOptionItemLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export {
  SelectContainer,
  StyledInputContainer,
  StyledCloseContainer,
  StyledOptionItemContainer,
  StyledOptionItem,
  StyledOptionsList,
  StyledSearchInputContainer,
  StyledLoaderEmptyContent,
  StyledOptionsScrollArea,
  StyledOptionItemLabelContainer,
};
